/**
 * ConfirmDialog: the one confirm-or-cancel dialog for destructive actions
 * (leave a team, transfer ownership, remove a member, revoke a key, delete
 * a tag or a team). The caller owns the open state and the mutation; this
 * renders the question, an optional extra control (`children`, e.g. a
 * type-the-name field), and the two buttons.
 *
 * The caller must own the mutation: a mutation started inside a dialog
 * loses its callbacks if the dialog unmounts mid-flight (React Query skips
 * per-call callbacks once nothing observes the mutation), so the error
 * would never reach the user. While the mutation runs the dialog also
 * refuses to close, so the answer always lands somewhere visible.
 */

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Optional: a question whose children say it all needs none. */
  description?: React.ReactNode
  confirmLabel: string
  /** The confirm button's label while the mutation runs. */
  pendingLabel: string
  isPending: boolean
  destructive?: boolean
  /** Keeps the confirm button off until the caller's own condition holds. */
  confirmDisabled?: boolean
  /** A failed attempt's message, shown above the buttons. */
  error?: string | null
  /** An extra control between the question and the buttons. */
  children?: React.ReactNode
  onConfirm: () => void
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pendingLabel,
  isPending,
  destructive = false,
  confirmDisabled = false,
  error = null,
  children,
  onConfirm,
}: ConfirmDialogProps): React.JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Escape, the overlay and the corner X all land here; none of them
        // may dismiss a request that is still running.
        if (!next && isPending) return
        onOpenChange(next)
      }}
    >
      {/* Without a description, say so to Radix, which warns about a dialog that names none. */}
      <DialogContent {...(description === undefined ? { 'aria-describedby': undefined } : {})}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description !== undefined && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {error !== null && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={isPending || confirmDisabled}
          >
            {isPending ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
